import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import DocumentationButton from "components/DocumentationButton";
import { EmptystateServicenowColored } from "components/icons/generated";
import TierInfo from "components/TierInfo";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useCanCreateIntegrations from "shared/Integrations/access/useCanCreateIntegrations";
import { AuthorizationScheme, BillingTierFeature } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";

import { showIntegrationDrawerForm } from "./components/IntegrationFormDrawer";
import { GET_ACCOUNT_EXTERNAL_INTEGRATIONS } from "./gql";
import PageLayout from "./PageLayout";
import { SERVICE_NOW_TABLE_CELLS } from "./Table/Cells";
import { SERVICE_NOW_TABLE_FILTERS_STORAGE_KEY, SORT_CONFIG } from "./Table/constants";
import { useSortingServiceNow } from "./Table/useSortingServiceNow";
import useServiceNowTableStore from "./Table/useTableStore";
import { GetAccountExternalIntegrationsGql } from "./types";

const billingTierFeature = BillingTierFeature.ServiceNowIntegration;

const ServiceNowIntegrationsList = () => {
  const trackAnalytics = useAnalytics();
  const canCreateIntegrations = useCanCreateIntegrations(billingTierFeature);

  const { loading, data } = useQuery<GetAccountExternalIntegrationsGql>(
    GET_ACCOUNT_EXTERNAL_INTEGRATIONS
  );

  const integrations = useMemo(() => {
    return (
      data?.externalIntegrations.filter((integration) => integration.type === "SERVICE_NOW") || []
    );
  }, [data?.externalIntegrations]);

  const isLoginPolicyActive = data?.authorizationScheme === AuthorizationScheme.LoginPolicies;

  const handleOnCreateClick = (location: string) => {
    trackAnalytics("Create integration clicked", { buttonLocation: location });
    showIntegrationDrawerForm({ isLoginPolicyActive, trackAnalytics });
  };

  const docsUrl = useMemo(() => getDocsUrl("/integrations/external-integrations/servicenow"), []);

  const { filteredIntegrations } = useSortingServiceNow(integrations);

  return (
    <PageLayout
      onCreate={() => handleOnCreateClick("Header")}
      canCreateIntegrations={canCreateIntegrations}
    >
      <TierInfo
        type="callout"
        variant="danger"
        title={`Upgrade plan to use ServiceNow integrations`}
        feature={billingTierFeature}
        storageKey="integrations-servicenow-tier-info"
      >
        ServiceNow integrations are not available in your current plan. Upgrade to{" "}
        <strong>Enterprise</strong> plan to use the feature.
      </TierInfo>

      <TableSimple
        data={filteredIntegrations}
        loading={loading}
        store={useServiceNowTableStore}
        ariaLabel="ServiceNow integrations list"
        trackAnalytics={trackAnalytics}
        cellPerColumn={SERVICE_NOW_TABLE_CELLS}
        filtersStorageKey={SERVICE_NOW_TABLE_FILTERS_STORAGE_KEY}
        sortConfig={SORT_CONFIG}
        emptyState={
          <Box align="center" justify="center" grow="1" fullWidth>
            <EmptyState
              padding="large"
              icon={EmptystateServicenowColored}
              title="Integrate with ServiceNow"
            >
              <Box gap="medium">
                <DocumentationButton to={docsUrl} label="Documentation" />
                {canCreateIntegrations && (
                  <Button variant="primary" onPress={() => handleOnCreateClick("Empty state")}>
                    Create integration
                  </Button>
                )}
              </Box>
            </EmptyState>
          </Box>
        }
      />
    </PageLayout>
  );
};

export default ServiceNowIntegrationsList;
